import { useQuery } from "@tanstack/react-query";

import { defaultEntityLinkRouterHandler } from "helpers/routing/EntityLinkRouter/EntityLinkRouter.api.handler";
import { type IEntityRecord } from "helpers/routing/EntityLinkRouter/EntityLinkRouter.api.types";

import { routes } from "utilities/routes";

import { type ISharingConfig } from "commonComponents/entity/sharing/EntityShareButton/EntityShareButton";

// Builds an absolute share link for an entity. Delegates to routes.entityLink.getFullLink
// (origin + fixed /app/link/:externalId path) rather than string-replacing the current page's
// location.href: replacing `location.pathname` (which is just "/" at the app root) matches the
// FIRST "/" in "http://...", not the trailing path, silently mangling the link into something like
// "http:/app/link/<id>/<host>/" whenever the share button is used from the root route.
export function getShareUrl(record: IEntityRecord) {
    return routes.entityLink.getFullLink(record.externalId);
}

export const getShareLink = async (config: ISharingConfig) => {
    const { overrideGetEntityRecord, entity } = config;
    let entityRecord: IEntityRecord;
    if (overrideGetEntityRecord) {
        entityRecord = await overrideGetEntityRecord(entity);
    } else {
        entityRecord = await defaultEntityLinkRouterHandler.insert(entity);
    }

    return getShareUrl(entityRecord);
};

// Wrap getShareLink around tanstack useQuery
export const useGetShareLink = (shareConfig: ISharingConfig | undefined) => {
    return useQuery({
        queryKey: ["getShareLink", shareConfig],
        queryFn: async () => {
            if (!shareConfig) {
                throw new Error("Share config was not defined");
            }
            return await getShareLink(shareConfig);
        },
        retry: 2,
        enabled: false,
    });
};
